import type { Card, Suit } from '../../engine/card.js';
import { color } from '../../engine/card.js';
import type { GameState, Pile } from '../../engine/game-state.js';
import { topCard } from '../../engine/game-state.js';
import type { Move } from '../../engine/moves.js';
import type { SolitaireRules } from '../../engine/rules.js';

export const TABLEAU_IDS = ['t1', 't2', 't3', 't4', 't5', 't6', 't7'] as const;
export const FOUNDATION_IDS = ['f-spades', 'f-hearts', 'f-diamonds', 'f-clubs'] as const;
const FOUNDATION_SUIT: Record<(typeof FOUNDATION_IDS)[number], Suit> = {
  'f-spades': 'spades',
  'f-hearts': 'hearts',
  'f-diamonds': 'diamonds',
  'f-clubs': 'clubs',
};

function emptyPile(id: string, type: Pile['type']): Pile {
  return { id, type, cards: [] };
}

// Yukon deal: all 52 cards go to the tableau, no stock/waste. Column 1 gets a
// single face-up card; columns 2-7 each get an increasing face-down base
// (1,2,3,4,5,6) topped with 5 face-up cards. 21 face-down + 31 face-up = 52.
function setupYukon(deck: Card[]): GameState {
  const piles: Record<string, Pile> = {};
  for (const id of TABLEAU_IDS) piles[id] = emptyPile(id, 'tableau');
  for (const id of FOUNDATION_IDS) piles[id] = emptyPile(id, 'foundation');

  const d = deck.slice();
  for (let i = 0; i < TABLEAU_IDS.length; i++) {
    const downCount = i; // 0,1,2,3,4,5,6
    const upCount = i === 0 ? 1 : 5; // column 1 keeps a lone card
    const pile = piles[TABLEAU_IDS[i]!]!;
    for (let k = 0; k < downCount; k++) {
      const c = d.shift()!;
      c.faceUp = false;
      pile.cards.push(c);
    }
    for (let k = 0; k < upCount; k++) {
      const c = d.shift()!;
      c.faceUp = true;
      pile.cards.push(c);
    }
  }

  return { piles, movesCount: 0 };
}

function isFoundationFor(pile: Pile, suit: Suit): boolean {
  return pile.type === 'foundation' && FOUNDATION_SUIT[pile.id as keyof typeof FOUNDATION_SUIT] === suit;
}

function canPlaceOnFoundation(pile: Pile, card: Card): boolean {
  if (!isFoundationFor(pile, card.suit)) return false;
  const top = topCard(pile);
  if (!top) return card.rank === 1;
  return card.rank === top.rank + 1;
}

function canPlaceOnTableau(pile: Pile, card: Card): boolean {
  if (pile.type !== 'tableau') return false;
  const top = topCard(pile);
  if (!top) return card.rank === 13;
  if (!top.faceUp) return false;
  return color(card) !== color(top) && card.rank === top.rank - 1;
}

export const yukonRules: SolitaireRules = {
  id: 'yukon',

  setup(deck) {
    return setupYukon(deck);
  },

  canMove(state, move: Move) {
    const from = state.piles[move.from];
    const to = state.piles[move.to];
    if (!from || !to) return false;
    if (from === to) return false;

    const idx = from.cards.findIndex((c) => c.id === move.cardId);
    if (idx === -1) return false;
    const moving = from.cards.slice(idx);

    const head = moving[0]!;
    if (!head.faceUp) return false;

    if (to.type === 'foundation') {
      return moving.length === 1 && canPlaceOnFoundation(to, head);
    }
    if (to.type === 'tableau') {
      // The Yukon rule: any face-up card moves together with every card piled
      // on top of it, regardless of whether they form a sequence. Only the
      // grabbed card has to land legally on the destination.
      return canPlaceOnTableau(to, head);
    }
    return false;
  },

  isWon(state) {
    return FOUNDATION_IDS.every((id) => state.piles[id]!.cards.length === 13);
  },

  isLost(state) {
    if (yukonRules.isWon(state)) return false;
    return !hasAnyMove(state);
  },

  afterMove(state, move) {
    const from = state.piles[move.from];
    if (from && from.type === 'tableau') {
      const top = topCard(from);
      if (top && !top.faceUp) top.faceUp = true;
    }
  },
};

export function hasAnyMove(state: GameState): boolean {
  const pileIds = Object.keys(state.piles);
  for (const fromPile of Object.values(state.piles)) {
    for (const card of fromPile.cards) {
      if (!card.faceUp) continue;
      for (const toId of pileIds) {
        if (toId === fromPile.id) continue;
        if (yukonRules.canMove(state, { from: fromPile.id, to: toId, cardId: card.id })) {
          return true;
        }
      }
    }
  }
  return false;
}
